const MIN_TRACK_POINTS = 2
const MAP_EDGE_LONGITUDE = 180
const MAP_WRAP_DELTA_LONGITUDE = 180
export const WORLD_COPY_LONGITUDE_OFFSETS = [-360, 0, 360] as const
const WORLD_LONGITUDE_WIDTH = 360

type LongitudeBounds = {
	getWest: () => number
	getEast: () => number
}

export type DrawableTrackPoint = {
	latitude: number
	longitude: number
}

export const isValidDrawableTrackPoint = (point: DrawableTrackPoint) =>
	Number.isFinite(point.latitude) === true &&
	Number.isFinite(point.longitude) === true &&
	point.latitude >= -90 &&
	point.latitude <= 90 &&
	point.longitude >= -180 &&
	point.longitude <= 180

const interpolateLatitudeAtLongitude = (
	from: DrawableTrackPoint,
	to: DrawableTrackPoint,
	longitude: number
) => {
	const longitudeDelta = to.longitude - from.longitude
	if (longitudeDelta === 0) {
		return from.latitude
	}

	const ratio = (longitude - from.longitude) / longitudeDelta
	return from.latitude + (to.latitude - from.latitude) * ratio
}

const pushDrawableSegment = (segments: DrawableTrackPoint[][], segment: DrawableTrackPoint[]) => {
	if (segment.length >= MIN_TRACK_POINTS) {
		segments.push(segment)
	}
}

export const splitTrackPointsAtMapEdges = (
	points: DrawableTrackPoint[]
): DrawableTrackPoint[][] => {
	const segments: DrawableTrackPoint[][] = []
	let currentSegment: DrawableTrackPoint[] = []

	for (const point of points) {
		if (isValidDrawableTrackPoint(point) === false) {
			pushDrawableSegment(segments, currentSegment)
			currentSegment = []
			continue
		}

		const previousPoint = currentSegment[currentSegment.length - 1]
		if (previousPoint === undefined) {
			currentSegment.push(point)
			continue
		}

		const longitudeDelta = point.longitude - previousPoint.longitude
		if (Math.abs(longitudeDelta) <= MAP_WRAP_DELTA_LONGITUDE) {
			currentSegment.push(point)
			continue
		}

		const crossesEastMapEdge = longitudeDelta < -MAP_WRAP_DELTA_LONGITUDE
		const edgeLongitude = crossesEastMapEdge ? MAP_EDGE_LONGITUDE : -MAP_EDGE_LONGITUDE
		const wrappedEdgeLongitude = crossesEastMapEdge ? -MAP_EDGE_LONGITUDE : MAP_EDGE_LONGITUDE
		const unwrappedPoint = {
			...point,
			longitude: point.longitude + (crossesEastMapEdge === true ? 360 : -360),
		}
		const edgeLatitude = interpolateLatitudeAtLongitude(
			previousPoint,
			unwrappedPoint,
			edgeLongitude
		)

		// Split the visual leg at the repeated world edge, so Leaflet does not draw a
		// long artificial chord across the map between +180 and -180 longitude.
		currentSegment.push({
			latitude: edgeLatitude,
			longitude: edgeLongitude,
		})
		pushDrawableSegment(segments, currentSegment)
		currentSegment = [
			{
				latitude: edgeLatitude,
				longitude: wrappedEdgeLongitude,
			},
			point,
		]
	}

	pushDrawableSegment(segments, currentSegment)
	return segments
}

export const getWorldCopyLongitudeOffsets = (bounds: LongitudeBounds | undefined): number[] => {
	if (bounds === undefined) {
		return [...WORLD_COPY_LONGITUDE_OFFSETS]
	}

	const west = bounds.getWest()
	const east = bounds.getEast()
	if (Number.isFinite(west) === false || Number.isFinite(east) === false) {
		return [...WORLD_COPY_LONGITUDE_OFFSETS]
	}

	const firstOffset =
		Math.floor((west - MAP_EDGE_LONGITUDE) / WORLD_LONGITUDE_WIDTH) * WORLD_LONGITUDE_WIDTH
	const lastOffset =
		Math.ceil((east + MAP_EDGE_LONGITUDE) / WORLD_LONGITUDE_WIDTH) * WORLD_LONGITUDE_WIDTH
	const offsets: number[] = []

	for (
		let longitudeOffset = firstOffset;
		longitudeOffset <= lastOffset;
		longitudeOffset += WORLD_LONGITUDE_WIDTH
	) {
		offsets.push(longitudeOffset)
	}

	return offsets.length > 0 ? offsets : [...WORLD_COPY_LONGITUDE_OFFSETS]
}

export const getWorldCopyPoints = (
	point: DrawableTrackPoint,
	longitudeOffsets: readonly number[] = WORLD_COPY_LONGITUDE_OFFSETS
): DrawableTrackPoint[] => {
	if (isValidDrawableTrackPoint(point) === false) {
		return []
	}
	return longitudeOffsets.map((longitudeOffset) => ({
		latitude: point.latitude,
		longitude: point.longitude + longitudeOffset,
	}))
}

export const getWorldCopyTrackSegments = (
	points: DrawableTrackPoint[],
	longitudeOffsets: readonly number[] = WORLD_COPY_LONGITUDE_OFFSETS
): DrawableTrackPoint[][] =>
	splitTrackPointsAtMapEdges(points).flatMap((trackPointSegment) =>
		longitudeOffsets.map((longitudeOffset) =>
			trackPointSegment.map((point) => ({
				latitude: point.latitude,
				longitude: point.longitude + longitudeOffset,
			}))
		)
	)

export const longitudesReferToSameWrappedPoint = (
	leftLongitude: number,
	rightLongitude: number
) => {
	const longitudeDelta = Math.abs(leftLongitude - rightLongitude) % 360
	return longitudeDelta === 0 || longitudeDelta === 360
}
